import React, { useState, useEffect } from 'react';
import { X, ChevronRight, Send, ClipboardList, Sparkles } from 'lucide-react';
import { BRAND } from '../brand';

/**
 * Tour de bienvenida la primera vez que el cliente entra al dashboard.
 * Se guarda en localStorage para no repetirlo.
 */
const STORAGE_KEY = 'girosbol_tour_completed_v2';

const STEPS = [
  {
    icon: Sparkles,
    title: `¡Bienvenido a ${BRAND.name}!`,
    body: 'Te mostramos en 20 segundos cómo enviar dinero a tu familia de forma rápida y segura.',
    cta: 'Empezar tour',
  },
  {
    icon: Send,
    title: 'Enviar dinero en 3 toques',
    body: 'Toca "Enviar" o "Enviar dinero ahora" y un asistente te guía: elige monto, beneficiario, sube el comprobante y listo.',
    cta: 'Siguiente',
  },
  {
    icon: ClipboardList,
    title: 'Sigue cada envío',
    body: 'En "Historial" ves el estado de cada operación: pendiente, en proceso o pagado. Te avisamos cuando llega a destino.',
    cta: 'Siguiente',
  },
  {
    icon: Sparkles,
    title: '¡Listo, ya puedes enviar!',
    body: 'Cualquier duda, escríbenos por WhatsApp 24/7. Estamos para ayudarte.',
    cta: 'Empezar a enviar',
  },
];

const OnboardingTour = ({ onClose }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Mostrar solo si nunca se completó
    if (typeof window !== 'undefined' && !window.localStorage.getItem(STORAGE_KEY)) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const finish = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    }
    setVisible(false);
    if (onClose) onClose();
  };

  const next = () => {
    if (stepIndex < STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      finish();
    }
  };

  if (!visible) return null;

  const step = STEPS[stepIndex];
  const Icon = step.icon;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="onboarding-tour-overlay"
    >
      <div
        className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md p-6 relative animate-in slide-in-from-bottom-4"
        data-testid="onboarding-tour-card"
      >
        <button
          onClick={finish}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition"
          aria-label="Cerrar tour"
          data-testid="onboarding-tour-close"
        >
          <X size={16} className="text-gray-600" />
        </button>

        {/* Progress dots */}
        <div className="flex gap-1.5 mb-5 justify-center">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === stepIndex ? 'w-8 bg-green-500' : i < stepIndex ? 'w-1.5 bg-green-300' : 'w-1.5 bg-gray-200'
              }`}
            />
          ))}
        </div>

        <div className="text-center mb-5">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mb-4">
            <Icon size={32} className="text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2" data-testid="onboarding-tour-title">
            {step.title}
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed px-2">{step.body}</p>
        </div>

        <button
          onClick={next}
          className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 shadow-md transition"
          data-testid="onboarding-tour-next-btn"
        >
          {step.cta}
          {!isLast && <ChevronRight size={18} />}
        </button>

        {!isFirst && !isLast && (
          <button
            onClick={finish}
            className="w-full mt-2 text-gray-400 hover:text-gray-600 text-sm py-1.5"
            data-testid="onboarding-tour-skip"
          >
            Saltar tour
          </button>
        )}
      </div>
    </div>
  );
};

export default OnboardingTour;
